import { WButton } from "@/components/button/WButton.tsx";
import { EnumBadge, type EnumProps } from "@/components/EnumBadge.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { EmptyRow, WTable, WTableLoader } from "@/components/table/WTable.tsx";
import type { StatusHistoryPage } from "@/modules/billing/billingStatusHistoryQuery.ts";
import { OngoingBalance } from "@/modules/billing/OngoingBalance.tsx";
import { eventTypeLabels, noHistoryText } from "@/modules/billing/statusHistory/statusHistoryLabels.tsx";
import { ErrorRow } from "@/modules/error/ErrorFallback.tsx";
import { Plural, Trans } from "@lingui/react/macro";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import type {
    BillingAccountStatus,
    BillingAccountStatusEvent,
    RestrictionLevel,
} from "@warrenio/api-spec/spec.oats.gen";
import { getOwn, hasOwn } from "@warrenio/utils/collections/getOwnProperty";
import type { ReactNode } from "react";

type StatusHistoryQuery<T> = Pick<
    UseInfiniteQueryResult<InfiniteData<StatusHistoryPage<T>>>,
    "data" | "error" | "hasNextPage" | "fetchNextPage"
>;

export interface StatusHistoryTableProps<T> {
    query: StatusHistoryQuery<T>;
    head: ReactNode;
    children: (item: T, index: number) => ReactNode;
}

/** Table of status events paged on the server, with a "Load more" button for the next page. */
export function StatusHistoryTable<T>({ query, head, children }: StatusHistoryTableProps<T>) {
    const { data, error, hasNextPage, fetchNextPage } = query;
    const items = data?.pages.flatMap((page) => page.items);

    async function loadMore() {
        await fetchNextPage({ throwOnError: true });
    }

    return (
        <WTable
            afterTable={
                hasNextPage ? (
                    <WButton color="primary" size="bar" variant="border" action={loadMore}>
                        <Trans comment="Button action: load the next rows of the list">Load more</Trans>
                    </WButton>
                ) : undefined
            }
        >
            {head}
            <tbody>
                {items === undefined ? (
                    error != null ? (
                        <tr>
                            <td colSpan={99}>
                                <ErrorRow error={error} tags="StatusHistoryTable" />
                            </td>
                        </tr>
                    ) : (
                        <WTableLoader />
                    )
                ) : items.length === 0 ? (
                    <EmptyRow>{noHistoryText}</EmptyRow>
                ) : (
                    items.map((item, index) => children(item, index))
                )}
            </tbody>
        </WTable>
    );
}

/** Labels for the values of the restriction level and account status events */
export interface StatusValueLabels {
    levels: Record<RestrictionLevel, EnumProps>;
    statuses: Record<BillingAccountStatus, EnumProps>;
}

/** The old and new value of a status event, formatted according to the event type */
export function StatusEventChange({ event, labels }: { event: BillingAccountStatusEvent; labels: StatusValueLabels }) {
    return (
        <div className="flex flex-wrap items-center gap-1">
            <StatusEventValue event={event} value={event.old_value} labels={labels} />
            <span className="color-muted">→</span>
            <StatusEventValue event={event} value={event.new_value} labels={labels} />
        </div>
    );
}

function StatusEventValue({
    event: { event_type },
    value,
    labels,
}: {
    event: BillingAccountStatusEvent;
    value: string | null | undefined;
    labels: StatusValueLabels;
}) {
    if (value == null) {
        return "-";
    }

    switch (event_type) {
        case "RESTRICTION_LEVEL_CHANGED":
            return hasOwn(labels.levels, value) ? (
                <EnumBadge options={labels.levels} value={value as RestrictionLevel} />
            ) : (
                value
            );
        case "BALANCE_BECAME_NEGATIVE":
        case "BALANCE_BECAME_POSITIVE": {
            const balance = Number(value);
            return Number.isFinite(balance) ? <OngoingBalance value={balance} /> : value;
        }
        case "SUSPENDED":
        case "UNSUSPENDED":
            return getOwn(labels.statuses, value)?.label ?? value;
        case "SUSPEND_REASON_CHANGED":
            return value;
        default:
            // Unknown event type from a newer API version
            return value;
    }
}

export function StatusEventLabel({ event: { event_type } }: { event: BillingAccountStatusEvent }) {
    return getOwn(eventTypeLabels, event_type) ?? event_type;
}

function getDetailNumber(details: BillingAccountStatusEvent["details"], key: string) {
    const value = getOwn(details, key);
    return typeof value === "number" ? value : undefined;
}

/** Why the restriction level changed, including the values from `details` where available */
export function StatusEventReason({ event: { reason_code, details } }: { event: BillingAccountStatusEvent }) {
    switch (reason_code) {
        case null:
        case undefined:
            return "-";
        case "ACCOUNT_DELETED":
            return <Trans>Billing account deleted</Trans>;
        case "ACCOUNT_SUSPENDED":
            return <Trans>Billing account suspended</Trans>;
        case "ACCOUNT_PENDING_VALIDATION":
            return <Trans>Billing account waiting for validation</Trans>;
        case "NEGATIVE_BALANCE_DAYS": {
            const days = getDetailNumber(details, "days");
            const threshold = getDetailNumber(details, "threshold");
            return days !== undefined && threshold !== undefined ? (
                <Trans>
                    Balance negative for <Plural value={days} one="# day" other="# days" /> (limit:{" "}
                    <Plural value={threshold} one="# day" other="# days" />)
                </Trans>
            ) : (
                <Trans>Balance negative for too long</Trans>
            );
        }
        case "NO_VALID_PAYMENT_METHOD":
            return <Trans>No verified primary payment method</Trans>;
        case "BALANCE_POSITIVE":
            return <Trans>Balance became positive</Trans>;
        case "ADMIN_OVERRIDE":
            return <Trans>Set by an administrator</Trans>;
        case "TOPUP_THRESHOLD_REACHED":
        case "BELOW_TOPUP_THRESHOLD": {
            const topupSum = getDetailNumber(details, "topup_sum");
            const threshold = getDetailNumber(details, "threshold");
            const isReached = reason_code === "TOPUP_THRESHOLD_REACHED";
            if (topupSum === undefined || threshold === undefined) {
                return isReached ? <Trans>Top-up threshold reached</Trans> : <Trans>Below the top-up threshold</Trans>;
            }
            return isReached ? (
                <Trans>
                    Top-ups of <CurrencyBalance value={topupSum} /> reached the threshold of{" "}
                    <CurrencyBalance value={threshold} />
                </Trans>
            ) : (
                <Trans>
                    Top-ups of <CurrencyBalance value={topupSum} /> are below the threshold of{" "}
                    <CurrencyBalance value={threshold} />
                </Trans>
            );
        }
        case "POST_PAYMENT_DEFAULT":
            return <Trans>Default level for post-payment billing accounts</Trans>;
        default:
            // Unknown reason code from a newer API version
            return reason_code;
    }
}
